/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * HR case creation affordance — consent dialog, submission and outcome states.
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { ChatBubbleIcon, CheckCircledIcon, Cross2Icon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import {
  createHrCase,
  HrCaseError,
  HR_COMMUNITY_URL,
  HR_MANUAL_CASE_URL,
  HR_COMMENT_MAX_LENGTH,
  type HrCaseResult,
} from '../../../utils/rag/hrCase.service';
import type { ChatMessage } from '../../../utils/chat/chat.service';

const CONSENT_TEXT =
  "By selecting this option, you will allow the HR Help team to access your ID, UPIN, name, email address, question asked and the tool's answer. This information will enable the assigned team to support you effectively.";

type Phase = 'idle' | 'submitting' | 'success' | 'declined' | 'error';
type TerminalPhase = Extract<Phase, 'success' | 'declined' | 'error'>;

interface Outcome {
  phase: TerminalPhase;
  result?: HrCaseResult;
  errorMessage?: string;
  manualUrl?: string;
}

/**
 * Outcomes are cached per message so a remount (scrollback, mode toggle) can never
 * resurrect the button and let the user file a duplicate Salesforce ticket.
 */
const outcomeByMessageId = new Map<string, Outcome>();

interface CreateHrCaseProps {
  messageId: string;
  chatHistory: ChatMessage[];
  /** Correlation id from the stream, reused so backend logs tie the case to the answer. */
  requestId?: string;
  dark: boolean;
}

/**
 * The transcript sent to Salesforce: real turns only, ending with the assistant message
 * that carried the flag, so the HR agent sees the context the employee already provided.
 */
const toPayloadHistory = (history: ChatMessage[], messageId: string) => {
  const flaggedIndex = history.findIndex((m) => m.id === messageId);
  const upToFlagged = flaggedIndex >= 0 ? history.slice(0, flaggedIndex + 1) : history;
  return upToFlagged
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && !m.isError && !m.isStopped)
    .map((m) => ({
      role: m.role,
      content:
        typeof m.content === 'string'
          ? m.content
          : (m.content || [])
              .map((item: any) => (item.type === 'input_text' ? item.text || '' : ''))
              .filter(Boolean)
              .join(' '),
    }))
    .filter((m) => m.content);
};

const CreateHrCase: React.FC<CreateHrCaseProps> = ({ messageId, chatHistory, requestId, dark }) => {
  const { instance: msalInstance } = useMsal();
  const cached = outcomeByMessageId.get(messageId);
  const [phase, setPhase] = useState<Phase>(cached?.phase ?? 'idle');
  const [outcome, setOutcome] = useState<Outcome | null>(cached ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comment, setComment] = useState('');
  // Synchronous guard: state updates are async, so a keyboard repeat or rapid double-click
  // could otherwise dispatch twice and create two real Salesforce tickets.
  const inFlightRef = useRef(false);

  const isTerminal = phase === 'success' || phase === 'declined' || phase === 'error';

  const recordOutcome = useCallback(
    (next: Outcome) => {
      outcomeByMessageId.set(messageId, next);
      setOutcome(next);
      setPhase(next.phase);
    },
    [messageId],
  );

  const handleProceed = useCallback(async () => {
    if (inFlightRef.current || phase === 'submitting') return;
    inFlightRef.current = true;
    setPhase('submitting');
    try {
      const result = await createHrCase(msalInstance, toPayloadHistory(chatHistory, messageId), requestId, comment);
      recordOutcome({ phase: 'success', result });
    } catch (err) {
      const hrErr = err instanceof HrCaseError ? err : null;
      recordOutcome({
        phase: 'error',
        errorMessage: hrErr?.isAuthError
          ? 'Your session has expired. Please sign in again and try creating the case.'
          : undefined,
        manualUrl: hrErr?.manualCaseUrl ?? HR_MANUAL_CASE_URL,
      });
    }
    // inFlightRef is deliberately never reset: every terminal state is final, and there is
    // no idempotency key, so a second dispatch would file a duplicate ticket.
  }, [phase, msalInstance, chatHistory, messageId, requestId, comment, recordOutcome]);

  const handleDecline = useCallback(() => {
    recordOutcome({ phase: 'declined', manualUrl: HR_MANUAL_CASE_URL });
  }, [recordOutcome]);

  // Dismissal is blocked mid-submission so the user cannot lose sight of an in-flight case.
  const dismissDialog = useCallback(() => {
    if (phase === 'submitting') return;
    setDialogOpen(false);
  }, [phase]);

  useEffect(() => {
    if (!dialogOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissDialog();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen, dismissDialog]);

  const linkClass = 'underline hover:opacity-80';
  const linkStyle: React.CSSProperties = { color: dark ? '#93b8f5' : '#2563eb' };
  // Shared so the secondary buttons' hover state cannot drift from the disclaimer panel.
  const disclaimerBg = dark ? 'rgba(37,99,235,0.12)' : '#eff6ff';
  const manualUrl = outcome?.manualUrl || HR_MANUAL_CASE_URL;
  // case_number is a best-effort second Salesforce lookup; case_id still identifies a
  // valid case when it is missing, so never present this as a failure.
  const caseReference = outcome?.result?.caseNumber || outcome?.result?.caseId;
  const dialogTitle = phase === 'success' ? 'Thank You!' : 'Create HR Help Case';

  const manualTicketLink = (
    <a href={manualUrl} target="_blank" rel="noopener noreferrer" className={linkClass} style={linkStyle}>
      Create an HR Help ticket
    </a>
  );

  const secondaryButtonProps = {
    className: 'px-3 py-2 rounded text-sm font-medium transition-colors',
    style: {
      backgroundColor: 'transparent',
      border: 'none',
      color: 'var(--color-text)',
      fontFamily: '3MCircularTT',
      cursor: 'pointer',
    } as React.CSSProperties,
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.backgroundColor = disclaimerBg;
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    },
  };

  const renderDialogBody = () => {
    if (phase === 'submitting') {
      return (
        <div className="flex items-center gap-3">
          <span
            style={{
              width: 16,
              height: 16,
              border: '2px solid rgba(37,99,235,0.25)',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 1s linear infinite',
              flexShrink: 0,
            }}
          />
          <p className="text-sm m-0" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
            Creating your HR Help Case. This can take a while, please do not close this window.
          </p>
        </div>
      );
    }

    if (phase === 'success') {
      return (
        <div className="flex items-start gap-2">
          <CheckCircledIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
          <div className="text-sm" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
            <p className="m-0">
              Your HR Help Case has been created. Case number: <strong>{caseReference}</strong>. You will be contacted
              shortly.
            </p>
            <p className="mt-1 m-0">
              Visit{' '}
              <a
                href={HR_COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                style={linkStyle}
              >
                hrcommunitypage
              </a>{' '}
              to upload additional files or check the status of your tickets.
            </p>
          </div>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#b45309' }} />
          <p className="text-sm m-0" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
            {outcome?.errorMessage ||
              'We couldn\u2019t create your case automatically. You can create it manually instead: '}
            {manualTicketLink}
          </p>
        </div>
      );
    }

    if (phase === 'declined') {
      return (
        <p className="text-sm m-0" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
          No problem. If you&rsquo;d still like to raise a case, you can create one manually: {manualTicketLink}
        </p>
      );
    }

    return (
      <>
        <p className="text-sm m-0" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
          Do you want us to create an HR support case for you automatically?
        </p>
        <div
          className="mt-3 rounded-md p-3 text-sm"
          style={{
            backgroundColor: disclaimerBg,
            border: `1px solid ${dark ? 'rgba(37,99,235,0.35)' : '#bfdbfe'}`,
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT',
          }}
        >
          {CONSENT_TEXT}
        </div>
        {/* <label
          htmlFor={`hr-case-comment-${messageId}`}
          className="block mt-4 mb-1 text-sm font-medium"
          style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}
        >
          Additional input on the case
        </label>
        <textarea
          id={`hr-case-comment-${messageId}`}
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, HR_COMMENT_MAX_LENGTH))}
          maxLength={HR_COMMENT_MAX_LENGTH}
          placeholder="Type your comment here..."
          rows={4}
          className="w-full rounded-md p-3 text-sm resize-none"
          style={{
            backgroundColor: dark ? '#0d0d0d' : '#ffffff',
            border: `1px solid ${dark ? '#333' : '#d4d4d4'}`,
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT',
          }}
        />
        <div className="text-right text-xs mt-1" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
          {comment.length} / {HR_COMMENT_MAX_LENGTH}
        </div> */}
      </>
    );
  };

  return (
    <>
      {/* Once a terminal state is reached the button is gone for good, but the user must never
          be left without a route to support, so a compact reminder takes its place. */}
      {isTerminal ? (
        <p className="mt-4 mb-0 text-sm" style={{ color: 'var(--color-text)', opacity: 0.75 }}>
          {phase === 'success' ? (
            <>
              HR case <strong>{caseReference}</strong> created &mdash;{' '}
              <a
                href={HR_COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                style={linkStyle}
              >
                check its status
              </a>
              .
            </>
          ) : (
            <>Need to raise a case? {manualTicketLink}.</>
          )}
        </p>
      ) : (
        <>
          <p
            className="mt-4 mb-0 text-sm font-bold"
            style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}
          >
            Still need help? Create an HR support case:
          </p>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={phase === 'submitting'}
            className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              backgroundColor: '#2563eb',
              color: '#ffffff',
              border: 'none',
              fontFamily: '3MCircularTT',
              cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
            }}
          >
            <ChatBubbleIcon className="w-4 h-4" />
            {/* 3MCircularTT is top-heavy, so the label needs a nudge to optically centre on the icon */}
            <span style={{ position: 'relative', top: '1px' }}>Create HR Help Case</span>
          </button>
        </>
      )}

      {dialogOpen &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 30000 }}
            onClick={dismissDialog}
          >
            <div
              className="rounded-lg shadow-xl max-w-lg w-full mx-4"
              style={{
                backgroundColor: dark ? '#1a1a1a' : '#ffffff',
                border: `1px solid ${dark ? '#333' : '#e5e5e5'}`,
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={dialogTitle}
            >
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: `1px solid ${dark ? '#333' : '#e5e5e5'}` }}
              >
                <div
                  className="flex items-center gap-2"
                  style={{ color: phase === 'success' ? 'var(--color-text)' : '#2563eb' }}
                >
                  {phase !== 'success' && <ChatBubbleIcon className="w-5 h-5" />}
                  <span
                    className="text-lg font-semibold"
                    style={{ fontFamily: '3MCircularTT', position: 'relative', top: '1px' }}
                  >
                    {dialogTitle}
                  </span>
                </div>
                {phase !== 'submitting' && (
                  <button
                    type="button"
                    onClick={dismissDialog}
                    aria-label="Close"
                    className="p-1 rounded hover:opacity-70"
                    style={{ background: 'none', border: 'none', color: 'var(--color-text)', cursor: 'pointer' }}
                  >
                    <Cross2Icon className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="px-5 py-4">{renderDialogBody()}</div>

              {phase !== 'submitting' && (
                <div
                  className="flex justify-end items-center gap-3 px-5 py-4"
                  style={{ borderTop: `1px solid ${dark ? '#333' : '#e5e5e5'}` }}
                >
                  {isTerminal ? (
                    <button type="button" onClick={dismissDialog} {...secondaryButtonProps}>
                      Close
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleProceed}
                        className="px-4 py-2 rounded text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          border: 'none',
                          fontFamily: '3MCircularTT',
                          cursor: 'pointer',
                        }}
                      >
                        Yes, proceed
                      </button>
                      <button type="button" onClick={handleDecline} {...secondaryButtonProps}>
                        No, create manually
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CreateHrCase;
